import { useMutation, useQuery } from 'react-query';
import type { AxiosResponse } from 'axios';
import type { User } from '@src/types/user';
import useUserSession from '@src/hooks/useUserSession';
import { BACKEND_API } from './axiosConfig';

type OAuthTokenResponse = {
    access_token: string;
    expires_in: number;
    id_token: string;
    refresh_token: string;
    scope: string;
    token_type: string;
};

const useRedeemAuthorizationCodeMutation = (mutationOpts = undefined) => {
    return useMutation(({ code }: { code: string }) => {
        const redirectUri = `${window.location.origin}/login-callback`;
        return BACKEND_API.post<any, AxiosResponse<OAuthTokenResponse>>(
            'auth/redeem_authorization_code',
            {
                redirect_uri: redirectUri,
                code,
            },
            {
                skipAuth: true,
            },
        );
    }, mutationOpts);
};

export const useGetCurrentUserQuery = (queryOpts = {}) => {
    return useQuery(['getCurrentUser', 'me'], async () => {
        return (await BACKEND_API.get<any, AxiosResponse<User>>(
            '/me',
        )).data;
    }, {
        refetchOnMount: false, // Do not refetch unless explicitly invalidated
        ...queryOpts,
    });
};

export const useGetAssumedUserQuery = ({ userId }: { userId: number }, queryOpts = {}) => {
    return useQuery(['getCurrentUser', userId], async () => {
        return (await BACKEND_API.get<any, AxiosResponse<User>>(
            `/users/${userId}`,
        )).data;
    }, {
        refetchOnMount: false, // Do not refetch unless explicitly invalidated
        ...queryOpts,
    });
};

export const useAlloyStatus = (queryOpts = undefined) => {
    const { user } = useUserSession();
    return useQuery(['getAlloyStatus', user.id], async () => {
        return (await BACKEND_API.get<any, AxiosResponse<User>>(
            `/alloy/update-status?uid=${user.id}`,
        )).data;
    }, queryOpts);
};

export default useRedeemAuthorizationCodeMutation;
